import React, { createContext, ReactNode, useContext } from 'react';
import { StoreSectionValidation, useStoreSectionValidation } from './StoreSection';
import { QuestionSectionValidation, useQuestionSectionValidation } from './QuestionSection';
import { usePageDataStore } from '../pageStore';

interface ValidationContextType {
    storeSectionValidation: StoreSectionValidation;
    questionSectionValidation: QuestionSectionValidation;
}

const ValidationContext = createContext<ValidationContextType | null>(null);

export const ValidationProvider = ({ children }: { children: ReactNode }) => {
    const out = {
        storeSectionValidation: useStoreSectionValidation(),
        questionSectionValidation: useQuestionSectionValidation(),
    };

    return <ValidationContext.Provider value={out}>{children}</ValidationContext.Provider>;
};

export const useValidator = () => {
    const context = useContext(ValidationContext);

    const pageData = usePageDataStore(state => state.pageData);

    if (!context) {
        throw new Error('useValidationContext must be used within a ValidationProvider');
    }

    const validators = {
        storeSectionValidation: context.storeSectionValidation,
        questionSectionValidation: context.questionSectionValidation,
    };

    const reset = () => {
        validators.storeSectionValidation.reset(pageData.storePresentation);
        validators.questionSectionValidation.reset(pageData.product.questions || []);
    };

    const validate = async () => {
        const allValid = await Promise.allSettled(Object.values(validators).map(validator => validator.trigger()));
        return allValid.every(
            (valid: PromiseSettledResult<boolean>) => valid.status === 'fulfilled' && valid.value === true
        );
    };

    return {
        reset,
        validate,
        ...validators,
    };
};
